import React, { FC, useEffect, useMemo, useState } from "react";
import {
  LuDownload,
  LuEye,
  LuFolderInput,
  LuFolderPlus,
  LuLink,
  LuPencil,
  LuTrash2,
  LuUpload,
  LuX,
} from "react-icons/lu";
import { Link, useParams } from "react-router-dom";

import { LottieLoading } from "../../components/graphics/LottieLoading";
import { useGetClientPortalQuery } from "../../slices/ClientPortalSlice";
import {
  downloadZip,
  useDeleteFilesMutation,
  useGetClientFilesQuery,
  useGetWorkFilesListQuery,
  useMoveFilesMutation,
} from "../../slices/DocumentsSlice";
import { useGetWorkQuery } from "../../slices/WorkSlice";
import { BillingMenuItem } from "../billing/ui/BillingUi";
import {
  DeleteFilesModal,
  FolderFormModal,
  LinkWorkModal,
  RenameFileModal,
  UploadFilesModal,
} from "./FileDialogs";
import { FileItem } from "./files.types";
import { refKey, sortFiles, toRef } from "./files.utils";
import { clientLink, FileTable } from "./FileTable";
import { FolderPickerModal } from "./FolderPicker";
import {
  ErrorText,
  errorText,
  PRIMARY_BUTTON,
  SECONDARY_BUTTON,
} from "./ui/FilesUi";
import { useFileOpener } from "./useFileOpener";

type Dialog =
  | { type: "upload"; files?: File[] }
  | { type: "folder" }
  | { type: "move"; files: FileItem[] }
  | { type: "link"; files: FileItem[] }
  | { type: "rename"; file: FileItem }
  | { type: "delete"; files: FileItem[] }
  | null;

/**
 * A work item's Files tab: its older work files plus the client's files linked
 * to it. New uploads are filed with the client (optionally in a folder).
 */
export const WorkFilesTab: FC = () => {
  const { workId = "" } = useParams();
  const { data: work } = useGetWorkQuery({ workId }, { skip: !workId });
  const customerId = work?.customerId || "";
  const {
    data: files = [],
    isLoading,
    isError,
    error: loadError,
    refetch,
  } = useGetWorkFilesListQuery(workId, {
    skip: !workId,
    refetchOnMountOrArgChange: true,
  });
  const { data: client } = useGetClientFilesQuery(customerId, {
    skip: !customerId,
  });
  const { data: portal } = useGetClientPortalQuery(
    { customerId },
    { skip: !customerId },
  );
  const [moveFiles, { isLoading: moving }] = useMoveFilesMutation();
  const [deleteFiles, { isLoading: deleting }] = useDeleteFilesMutation();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [dialog, setDialog] = useState<Dialog>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [dragging, setDragging] = useState(false);
  const { openFile, downloadFile, preview } = useFileOpener(setError);

  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(""), 4000);
    return () => clearTimeout(timer);
  }, [notice]);

  const rows = useMemo(() => sortFiles(files, "newest"), [files]);
  const selectedFiles = rows.filter((f) => selected.has(refKey(toRef(f))));
  const folders = client?.folders || [];
  // The client's list has active and archived work (not deleted): keep this one in it anyway.
  const uploadWorks = useMemo(() => {
    const list = client?.works || [];
    return work && !list.some((w) => w.id === workId)
      ? [{ id: workId, name: work.name }, ...list]
      : list;
  }, [client?.works, work, workId]);

  const menu = (file: FileItem): BillingMenuItem[] => {
    const items: BillingMenuItem[] = [
      {
        label: "Preview",
        icon: <LuEye size={15} />,
        onClick: () => openFile(file),
      },
      {
        label: "Download",
        icon: <LuDownload size={15} />,
        onClick: () => downloadFile(file),
      },
      {
        label: "Rename",
        icon: <LuPencil size={15} />,
        onClick: () => setDialog({ type: "rename", file }),
      },
    ];
    if (customerId) {
      items.push({
        label: "File to…",
        icon: <LuFolderInput size={15} />,
        onClick: () => setDialog({ type: "move", files: [file] }),
      });
      items.push({
        label: "Work item…",
        icon: <LuLink size={15} />,
        onClick: () => setDialog({ type: "link", files: [file] }),
      });
    }
    items.push({
      label: "Delete",
      icon: <LuTrash2 size={15} />,
      danger: true,
      onClick: () => setDialog({ type: "delete", files: [file] }),
    });
    return items;
  };

  if (isLoading) return <LottieLoading />;

  return (
    <div
      className={"font-Manrope text-[#1F2433]"}
      onDragOver={(event) => {
        if (event.dataTransfer.types.includes("Files")) {
          event.preventDefault();
          setDragging(true);
        }
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(event) => {
        if (!event.dataTransfer.files?.length) return;
        event.preventDefault();
        setDragging(false);
        setDialog({
          type: "upload",
          files: Array.from(event.dataTransfer.files),
        });
      }}>
      <div className={"mb-3 flex flex-wrap items-center gap-2"}>
        <div className={"text-[13px] text-[#667085]"}>
          {customerId ? (
            <>
              Files for this work item. They are also in{" "}
              {client && (
                <Link
                  className={"font-semibold text-[#5A3EEB] hover:underline"}
                  to={clientLink({ ...client, customerId: client.customerId })}>
                  {client.name}’s Files
                </Link>
              )}
              .
            </>
          ) : (
            "Files for this work item."
          )}
        </div>
        <span className={"flex-1"} />
        {customerId && (
          <button
            type={"button"}
            className={SECONDARY_BUTTON}
            onClick={() => setDialog({ type: "folder" })}>
            <LuFolderPlus size={15} />
            New folder
          </button>
        )}
        <button
          type={"button"}
          className={PRIMARY_BUTTON}
          onClick={() => setDialog({ type: "upload" })}>
          <LuUpload size={15} />
          Upload
        </button>
      </div>
      <div
        className={`rounded-2xl border bg-white px-3.5 py-1.5 ${
          dragging
            ? "border-[#6B4EFF] ring-2 ring-[#6B4EFF]/30"
            : "border-[#E6E9EF]"
        }`}>
        {isError ? (
          <div className={"py-10 text-center text-[13px]"}>
            {errorText(loadError, "The files couldn't be loaded.")}{" "}
            <button
              type={"button"}
              className={"font-bold text-[#5A3EEB] underline"}
              onClick={() => refetch()}>
              Try again
            </button>
          </div>
        ) : (
          <FileTable
            files={rows}
            columns={{
              location: !!customerId,
              visibility: !!customerId,
              addedBy: true,
              added: true,
              size: true,
            }}
            selected={selected}
            onSelect={setSelected}
            onOpen={openFile}
            menu={menu}
            empty={
              <div className={"py-12 text-center text-[13px] text-[#667085]"}>
                No files yet. Drag files here or click Upload.
              </div>
            }
          />
        )}
      </div>
      {selectedFiles.length > 0 && (
        <div
          className={
            "sticky bottom-3 mt-3 flex flex-wrap items-center gap-1.5 rounded-xl bg-[#1F2433] px-3.5 py-2 text-[13px] font-bold text-white shadow-lg"
          }>
          <span className={"mr-2"}>{selectedFiles.length} selected</span>
          <span className={"flex-1"} />
          {customerId && (
            <button
              type={"button"}
              className={
                "rounded-md px-2.5 py-1 font-semibold hover:bg-white/10"
              }
              onClick={() => setDialog({ type: "move", files: selectedFiles })}>
              File to…
            </button>
          )}
          <button
            type={"button"}
            className={"rounded-md px-2.5 py-1 font-semibold hover:bg-white/10"}
            onClick={async () => {
              try {
                await downloadZip(selectedFiles.map(toRef));
              } catch (e) {
                setError(errorText(e));
              }
            }}>
            Download (.zip)
          </button>
          <button
            type={"button"}
            className={
              "rounded-md px-2.5 py-1 font-semibold text-[#FF9EA1] hover:bg-white/10"
            }
            onClick={() => setDialog({ type: "delete", files: selectedFiles })}>
            Delete
          </button>
          <button
            type={"button"}
            aria-label={"Clear selection"}
            className={"ml-1 rounded p-1 hover:bg-white/10"}
            onClick={() => setSelected(new Set())}>
            <LuX size={15} />
          </button>
        </div>
      )}
      {notice && (
        <div
          role={"status"}
          className={
            "mt-3 rounded-lg bg-[#E3F6EE] px-3 py-2 text-[13px] font-semibold text-[#0E8A5F]"
          }>
          {notice}
        </div>
      )}
      <ErrorText>{error}</ErrorText>

      {preview}
      {dialog?.type === "upload" && (
        <UploadFilesModal
          customerId={customerId || null}
          clientName={work?.name}
          folders={folders}
          works={uploadWorks}
          workId={workId}
          lockWork={true}
          initialFiles={dialog.files}
          portalActive={!!portal?.people?.some((p) => p.status === "active")}
          onClose={() => setDialog(null)}
          onDone={(count) =>
            setNotice(`Uploaded ${count} file${count === 1 ? "" : "s"}.`)
          }
        />
      )}
      {dialog?.type === "folder" && customerId && (
        <FolderFormModal
          customerId={customerId}
          folders={folders}
          chooseParent={true}
          onClose={() => setDialog(null)}
          onSaved={() =>
            setNotice(
              "Folder created in the client's files. Upload or move files into it.",
            )
          }
        />
      )}
      {dialog?.type === "move" && customerId && (
        <FolderPickerModal
          title={`File ${
            dialog.files.length === 1
              ? `“${dialog.files[0].name}”`
              : `${dialog.files.length} files`
          }`}
          subtitle={client?.name}
          customerId={customerId}
          folders={folders}
          initial={dialog.files.length === 1 ? dialog.files[0].folderId : null}
          confirmLabel={"File here"}
          busy={moving}
          error={error}
          onClose={() => setDialog(null)}
          onPick={async (folderId) => {
            setError("");
            try {
              await moveFiles({
                items: dialog.files.map(toRef),
                folderId,
              }).unwrap();
              setDialog(null);
              setSelected(new Set());
              setNotice("Filed. The files stay linked to this work item.");
            } catch (e) {
              setError(errorText(e));
            }
          }}
        />
      )}
      {dialog?.type === "link" && (
        <LinkWorkModal
          files={dialog.files}
          works={client?.works || []}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.type === "rename" && (
        <RenameFileModal file={dialog.file} onClose={() => setDialog(null)} />
      )}
      {dialog?.type === "delete" && (
        <DeleteFilesModal
          title={
            dialog.files.length === 1
              ? "Delete this file?"
              : `Delete ${dialog.files.length} files?`
          }
          description={
            "Deleted files go to the recycle bin (Documents › Recycle bin) for 90 days, then they are erased."
          }
          busy={deleting}
          error={error}
          onClose={() => setDialog(null)}
          onConfirm={async () => {
            setError("");
            try {
              await deleteFiles(dialog.files.map(toRef)).unwrap();
              setDialog(null);
              setSelected(new Set());
              setNotice("Moved to the recycle bin.");
            } catch (e) {
              setError(errorText(e));
            }
          }}
        />
      )}
    </div>
  );
};

export default WorkFilesTab;
